import { useEffect, useState } from "react"
import { api } from "../../lib/api"
import type { Compra } from "../../types"
import { reais } from "../../lib/format"
import { Botao, Card, Carregando } from "../ui"

/** O que a página do Mercado Pago disse ao devolver a pessoa (`?status=`). Nulo = voltou por conta própria. */
export type EstadoDoRetorno = "sucesso" | "pendente" | "falha" | null

// Até quando esperar o webhook antes de dizer "pode fechar". O gateway avisa
// em segundos; a reconciliação cobre o resto em até 15 min.
const ESPERA_MAXIMA_MS = 90_000
const INTERVALO_MS = 3_000

/**
 * A volta da página do cartão. O Mercado Pago devolve a pessoa a
 * `/acesso?status=…&compra=…`, e esta tela fecha o ciclo: consulta a compra
 * até o webhook confirmar (quem confirma é ele, ou a reconciliação — nunca o
 * `?status`, que é só o que o navegador trouxe e qualquer um escreve na URL).
 *
 * Também é a tela que o `/acesso` retoma quando a pessoa voltou sem os
 * parâmetros (navegador embutido, sessão perdida) e há um cartão em aberto.
 */
export default function RetornoDoCartao({
  compra,
  estado,
  aoLiberar,
  aoPagarDeOutroJeito,
}: {
  compra: Compra
  estado: EstadoDoRetorno
  aoLiberar: () => void
  /** Volta à oferta para pagar por Pix (ou tentar o cartão de novo). */
  aoPagarDeOutroJeito: () => void
}) {
  const [status, setStatus] = useState(compra.status)
  const [parcelas, setParcelas] = useState(compra.parcelas)
  const [esperou, setEsperou] = useState(false)

  useEffect(() => {
    if (status !== "pendente") return undefined

    const inicio = Date.now()
    const intervalo = setInterval(async () => {
      try {
        const atual = await api.get<Compra>(`/compras/${compra.id}`)
        setStatus(atual.status)
        setParcelas(atual.parcelas)
        if (atual.status === "paga") {
          clearInterval(intervalo)
          setTimeout(aoLiberar, 1500)
          return
        }
      } catch {
        // Falha de rede no polling não é falha do pagamento: tenta de novo.
      }
      if (Date.now() - inicio > ESPERA_MAXIMA_MS) {
        clearInterval(intervalo)
        setEsperou(true)
      }
    }, INTERVALO_MS)

    return () => clearInterval(intervalo)
  }, [compra.id, status, aoLiberar])

  if (status === "paga") {
    return (
      <Card className="border-emerald-300 text-center">
        <p className="text-lg font-bold text-emerald-700">Pagamento confirmado</p>
        <p className="mt-1 text-sm text-slate-600">
          {reais(compra.valor_centavos)}
          {parcelas && parcelas > 1 ? ` em ${parcelas}× no cartão` : " no cartão"}. Seu acesso já está liberado, e o
          recibo chega por e-mail. Bons estudos.
        </p>
      </Card>
    )
  }

  if (status === "estornada") {
    return (
      <Card className="space-y-3 text-center">
        <p className="text-lg font-bold text-navy-900">Pagamento estornado</p>
        <p className="text-sm text-slate-600">
          O valor volta ao seu cartão pela operadora, no prazo dela. Se não era isso que você queria, fale com o
          suporte.
        </p>
        <Botao variante="fantasma" onClick={aoPagarDeOutroJeito}>
          Voltar
        </Botao>
      </Card>
    )
  }

  if (status === "cancelada" || estado === "falha") {
    return (
      <Card className="space-y-3 text-center">
        <p className="text-lg font-bold text-navy-900">O pagamento não foi aprovado</p>
        <p className="text-sm text-slate-600">
          Nada foi cobrado. Você pode tentar com outro cartão ou pagar por Pix — o QR code aparece na hora.
        </p>
        <Botao variante="compra" onClick={aoPagarDeOutroJeito}>
          Escolher como pagar
        </Botao>
      </Card>
    )
  }

  // Pendente: voltou do cartão (sucesso/pendente) ou retomou a compra em
  // aberto. Espera o webhook; depois do limite, diz que pode fechar.
  return (
    <Card className="space-y-4 text-center">
      <div>
        <h1 className="text-lg font-bold text-navy-900">{compra.produto}</h1>
        <p className="text-2xl font-extrabold text-navy-900">{reais(compra.valor_centavos)}</p>
      </div>

      {estado === null && !esperou ? (
        // Retomada sem vir da página do cartão: pode ter pago (webhook a
        // caminho) ou ter desistido no meio. Não finge que sabe.
        <p className="text-sm text-slate-600">
          Você começou um pagamento no cartão. Se já pagou, o acesso libera sozinho em instantes e o recibo chega por
          e-mail. Se não, dá para voltar ao pagamento ou escolher outro jeito.
        </p>
      ) : esperou ? (
        <>
          <p className="text-sm text-slate-600">
            Ainda não recebemos a confirmação da operadora. Se o pagamento foi aprovado, o acesso libera sozinho em
            alguns minutos e o recibo chega por e-mail — pode fechar esta tela.
          </p>
          <p className="text-xs text-slate-500">
            Se em uma hora nada chegar, fale com o suporte informando a compra nº {compra.id}.
          </p>
        </>
      ) : (
        <>
          <Carregando texto="Confirmando com a operadora…" />
          <p className="text-sm text-slate-600">
            Leva alguns segundos. O acesso libera sozinho assim que a confirmação chegar, mesmo que esta tela feche.
          </p>
        </>
      )}

      <div className="flex flex-wrap justify-center gap-2">
        {compra.checkout_url && estado !== "sucesso" && (
          <a
            href={compra.checkout_url}
            className="inline-flex items-center justify-center rounded-xl border border-navy-200 px-4 py-2 text-sm font-semibold text-navy-900"
          >
            Voltar ao pagamento no cartão
          </a>
        )}
        <button type="button" onClick={aoPagarDeOutroJeito} className="text-sm text-slate-600 underline">
          Pagar de outro jeito
        </button>
      </div>
    </Card>
  )
}
